import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Check, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  project: "",
  preferred_date: "",
  preferred_time: "",
  meeting_method: "",
  consent: false,
  hp: "",
};

const inputCls =
  "mt-2 w-full border-0 border-b border-[#c9c4bb] bg-transparent pb-2 font-display text-lg text-[#1c1c1a] outline-none transition-colors placeholder:font-body placeholder:text-sm placeholder:text-[#a5a096] focus:border-[#9e8d7b] [color-scheme:light]";
const labelCls = "font-body text-[10px] uppercase tracking-[0.2em] text-[#8a8375]";

export const RequestCallModal = () => {
  const { t } = useI18n();
  const rc = t.requestCall;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const openedAt = useRef(0);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) {
      openedAt.current = Date.now();
      setSubmitted(false);
      setForm(empty);
      setShowPrivacy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // anti-spam: honeypot or too-fast submit
    if (form.hp || Date.now() - openedAt.current < 1500) {
      setSubmitted(true);
      return;
    }
    if (!form.name || !form.email || !form.phone || !form.project || !form.consent) {
      toast.error(rc.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/call-request`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(rc.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="request-call-button"
          className="group inline-flex items-center gap-3 border border-[#9e8d7b] px-8 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-[#f4f4f0] transition-colors duration-300 hover:bg-[#9e8d7b] hover:text-[#1c1c1a]"
        >
          <CalendarClock className="h-4 w-4" strokeWidth={1.5} />
          {rc.trigger}
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid="request-call-modal"
        className="max-h-[90vh] w-[calc(100vw-2rem)] gap-0 overflow-y-auto rounded-none border border-[#dcdcd6] bg-[#f4f4f0] p-0 text-[#1c1c1a] sm:max-w-lg"
      >
        {submitted ? (
          <div className="flex flex-col items-center px-8 py-16 text-center" data-testid="request-call-success">
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#9e8d7b]">
              <Check className="h-6 w-6 text-[#9e8d7b]" strokeWidth={1.5} />
            </span>
            <DialogTitle className="mt-8 font-display text-3xl font-normal tracking-tight">
              {rc.title}
            </DialogTitle>
            <p className="mt-4 max-w-sm font-body text-base leading-relaxed text-[#5c5c57]">{rc.success}</p>
            <button
              onClick={() => setOpen(false)}
              data-testid="request-call-close"
              className="mt-9 bg-[#1c1c1a] px-8 py-3 font-body text-[11px] uppercase tracking-[0.22em] text-[#f4f4f0] transition-transform hover:-translate-y-0.5"
            >
              {rc.done}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-2 border-b border-[#dcdcd6] px-8 pb-6 pt-8 text-left">
              <DialogTitle className="font-display text-3xl font-normal tracking-tight">{rc.title}</DialogTitle>
              <DialogDescription className="font-body text-sm text-[#5c5c57]">{rc.subtitle}</DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} data-testid="request-call-form" className="space-y-6 px-8 py-8">
              {/* honeypot */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.hp}
                onChange={set("hp")}
                className="absolute left-[-9999px] h-0 w-0 opacity-0"
              />

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <label className="block">
                  <span className={labelCls}>{rc.name} *</span>
                  <input data-testid="rc-name" autoComplete="name" value={form.name} onChange={set("name")} placeholder={rc.namePh} className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>{rc.company}</span>
                  <input data-testid="rc-company" value={form.company} onChange={set("company")} placeholder={rc.companyPh} className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>{rc.email} *</span>
                  <input type="email" autoComplete="email" data-testid="rc-email" value={form.email} onChange={set("email")} placeholder={rc.emailPh} className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>{rc.phone} *</span>
                  <input type="tel" inputMode="tel" autoComplete="tel" data-testid="rc-phone" value={form.phone} onChange={set("phone")} placeholder={rc.phonePh} className={inputCls} />
                </label>
              </div>

              <label className="block">
                <span className={labelCls}>{rc.project} *</span>
                <textarea data-testid="rc-project" rows={3} value={form.project} onChange={set("project")} placeholder={rc.projectPh} className={`${inputCls} resize-none`} />
              </label>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <label className="block">
                  <span className={labelCls}>{rc.date}</span>
                  <input type="date" data-testid="rc-date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>{rc.time}</span>
                  <input type="time" data-testid="rc-time" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>{rc.method}</span>
                  <select data-testid="rc-method" value={form.meeting_method} onChange={set("meeting_method")} className={`${inputCls} cursor-pointer`}>
                    <option value="">{rc.methodPh}</option>
                    {rc.methodOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="rc-consent"
                    data-testid="rc-consent"
                    checked={form.consent}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))}
                    className="mt-0.5 border-[#9e8d7b] data-[state=checked]:bg-[#9e8d7b] data-[state=checked]:text-[#f4f4f0]"
                  />
                  <label htmlFor="rc-consent" className="font-body text-xs leading-relaxed text-[#5c5c57]">
                    {rc.consent}{" "}
                    <button type="button" onClick={() => setShowPrivacy((s) => !s)} className="underline underline-offset-2 hover:text-[#1c1c1a]">
                      {rc.privacyLink}
                    </button>
                  </label>
                </div>
                {showPrivacy && (
                  <p className="mt-3 border-l-2 border-[#dcdcd6] pl-4 font-body text-[11px] leading-relaxed text-[#8a8375]">
                    {rc.privacyText}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="rc-submit"
                className="group inline-flex w-full items-center justify-center gap-3 bg-[#1c1c1a] px-9 py-4 font-body text-[11px] uppercase tracking-[0.22em] text-[#f4f4f0] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
              >
                {loading ? rc.sending : rc.submit}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
